import { IUser } from '../../core/interfaces/user.interface';
import {
  getSeeRoles,
  getSeeUnits,
  getSeeUsers,
} from '../../core/components/main-layout-sidebar/utils';
import { Tutorial } from './tutorial.types';

/**
 * CATÁLOGO DECLARATIVO DE TUTORIALES DE SKULD ANALYST (SCG-368).
 *
 * Agregar un tutorial = agregar una entrada aquí + poner los `data-tour="..."`
 * correspondientes en el HTML del módulo. El motor no se toca.
 *
 * Reglas (las valida tutorials.registry.spec.ts):
 *  - Ids `namespace.modulo` únicos; `description` dice el OBJETIVO.
 *  - Máximo 6 pasos por tutorial.
 *  - `anchor` refiere a `[data-tour="<anchor>"]`. Si falta en el DOM, el motor
 *    salta el paso (no rompe).
 *  - `audiencia` decide a quién va: sin ella aplica a todos.
 *  - El ORDEN importa: entre tutoriales de la misma ruta se ofrece el primero
 *    elegible ('/dashboard' también coincide con '/dashboard-alerts').
 *  - Íconos: solo ligaduras que existan en la TTF local de Material Icons
 *    (assets/font/MaterialIcons-Regular.ttf, versión vieja: no hay 'hub',
 *    'smart_toy', 'checklist', 'restart_alt'…).
 */

// ── Audiencias (mismas condiciones que usa el menú lateral) ──────────────────

const unidades = (u: IUser): any[] => (u.role && u.role.units) || [];
const tieneUnidad = (u: IUser, code: string): boolean =>
  unidades(u).some((x) => x.code === code);
/** Flags de pestaña de la unidad (unit.model: grafo, timeline…). */
const unidadCon = (u: IUser, flag: string): boolean =>
  unidades(u).some((x) => !!x[flag]);

/** Tiene unidades (Módulo de investigación). */
const conUnidades = (u: IUser): boolean => unidades(u).length > 0;
/** Unidad de alertas (isFrd en UserService). */
const esAlertas = (u: IUser): boolean => tieneUnidad(u, 'ALT');
/** Unidad de Análisis (getSeeAna). */
const esAnalisis = (u: IUser): boolean => tieneUnidad(u, 'AN');
/** Una unidad de casos: ni AN (va aparte) ni ALT (va a /alerts). */
const esCasos = (u: IUser): boolean =>
  unidades(u).some((x) => x.code !== 'AN' && x.code !== 'ALT');
/** isRas: su única unidad es LDC. */
const esRas = (u: IUser): boolean =>
  unidades(u).length === 1 && tieneUnidad(u, 'LDC');

export const TUTORIALS: Tutorial[] = [
  // ── General ────────────────────────────────────────────────────────────────
  {
    // T1. '/dashboard' coincide también con '/dashboard-alerts' y
    // '/dashboard/...': es lo primero que se ofrece al entrar.
    id: 'general.inicio',
    category: 'General',
    title: 'Primeros pasos en Skuld Analyst',
    description:
      'Ubicarte en la aplicación: el menú lateral, las notificaciones y tu perfil.',
    icon: 'explore',
    routeMatch: '/dashboard',
    steps: [
      {
        title: 'Bienvenido a Skuld Analyst',
        body:
          'Aquí se gestionan las alertas, los casos de investigación y su análisis. Te mostramos en pocos pasos dónde está cada cosa.',
        placement: 'center',
      },
      {
        anchor: 'general.menu',
        title: 'Menú lateral',
        body:
          'Desde aquí entras a cada módulo: Inicio, Módulo de investigación (tus unidades), Análisis y Administración. <b>Solo ves lo que tu rol permite.</b>',
        placement: 'right',
      },
      {
        anchor: 'general.notificaciones',
        title: 'Notificaciones',
        body:
          'La campana avisa de asignaciones y cambios en tus casos y alertas. El número indica cuántas tienes sin leer.',
        placement: 'bottom',
      },
      {
        anchor: 'general.usuario',
        title: 'Tu usuario',
        body:
          'Con un clic en tu foto abres el menú con <b>Perfil</b> (tus datos, contraseña y pregunta de seguridad) y <b>Salir</b>.',
        placement: 'bottom',
      },
      {
        title: 'Tutoriales en tu Perfil',
        body:
          'Cada módulo te ofrecerá su recorrido la primera vez que entres. En <b>Perfil &gt; Tutoriales</b> puedes reiniciar, desactivar o reactivar cualquiera.',
        placement: 'center',
      },
    ],
  },

  // ── Alertas ────────────────────────────────────────────────────────────────
  {
    // T2. Va después de T1 en el registro: se ofrece cuando T1 ya no es
    // elegible (completado, desactivado o en enfriamiento).
    id: 'alertas.dashboard',
    category: 'Alertas',
    title: 'Dashboard de alertas',
    description:
      'Leer las estadísticas de alertas (nuevas, cerradas, prioridad, estado y canal) y las de trabajo del equipo.',
    icon: 'insert_chart',
    routeMatch: '/dashboard-alerts',
    audiencia: (u) => !!(u.role && u.role.skuld),
    steps: [
      {
        anchor: 'alertas.dash.tab-alertas',
        title: 'Estadísticas de alerta',
        body:
          'Esta pestaña resume el volumen de alertas. La de al lado muestra el trabajo del equipo.',
        placement: 'bottom',
      },
      {
        anchor: 'alertas.dash.resumen',
        title: 'Resumen y prioridad',
        body:
          'Indicadores rápidos, alertas nuevas contra cerradas y su distribución por prioridad.',
        placement: 'right',
      },
      {
        anchor: 'alertas.dash.estado',
        title: 'Estado y canal',
        body: 'Porcentaje de alertas por estado y de qué canal llegan.',
        placement: 'left',
      },
      {
        anchor: 'alertas.dash.ultimas',
        title: 'Últimas alertas',
        body: 'Las alertas más recientes, para entrar directo a las nuevas.',
        placement: 'top',
      },
      {
        anchor: 'alertas.dash.tab-trabajo',
        title: 'Estadísticas de trabajo',
        body:
          'Asignadas contra resueltas, tiempos de respuesta y reasignaciones: cómo va el equipo.',
        placement: 'bottom',
        clickAnchorOnEnter: true,
      },
    ],
  },

  {
    // T3. Bandeja de la unidad ALT (AlertContentComponent).
    id: 'alertas.bandeja',
    category: 'Alertas',
    title: 'Bandeja de alertas',
    description:
      'Encontrar, filtrar y tomar alertas, y seguirlas por estado hasta su cierre.',
    icon: 'inbox',
    routeMatch: '/alerts/',
    audiencia: esAlertas,
    steps: [
      {
        anchor: 'bandeja.buscador',
        title: 'Buscar y filtrar',
        body:
          'Busca por texto y abre los filtros con el ícono de ajustes. Los botones de la derecha filtran por tipo de alerta.',
        placement: 'bottom',
      },
      {
        anchor: 'bandeja.estados',
        title: 'Estados',
        body:
          'Sin asignar, Asignado, En proceso, Por aprobar, Finalizado y General. El número de cada pestaña es la cantidad de alertas.',
        placement: 'bottom',
      },
      {
        anchor: 'bandeja.lista',
        title: 'Lista de alertas',
        body:
          'Haz clic en una alerta para abrir su expediente. El ícono de la primera columna indica si está aprobada.',
        placement: 'top',
      },
    ],
  },

  // ── Casos ──────────────────────────────────────────────────────────────────
  {
    // T4. Tablero de una unidad de casos (/cases/:id).
    id: 'casos.tablero',
    category: 'Casos',
    title: 'Tablero de casos de tu unidad',
    description:
      'Crear casos, buscarlos y moverlos entre columnas según avanzan.',
    icon: 'view_column',
    routeMatch: '/cases/',
    audiencia: esCasos,
    steps: [
      {
        anchor: 'casos.unidad',
        title: 'Tu unidad',
        body: 'Estás en el tablero de esta unidad. Cada unidad tiene el suyo.',
        placement: 'bottom',
      },
      {
        // No aparece en Incidencias, CAE ni CAI aunque el rol pueda crear: si
        // falta el ancla, el paso se salta.
        anchor: 'casos.crear',
        title: 'Crear caso',
        body: 'Abre un caso nuevo en esta unidad.',
        placement: 'bottom',
        requiresPermission: 'createCases',
      },
      {
        anchor: 'casos.archivados',
        title: 'Casos archivados',
        body: 'Consulta los casos que ya se cerraron y archivaron.',
        placement: 'bottom',
      },
      {
        anchor: 'casos.resumen',
        title: 'Resumen de unidad',
        body: 'Indicadores de la unidad: casos por estado, por tipo y por analista.',
        placement: 'bottom',
      },
      {
        anchor: 'casos.buscador',
        title: 'Buscar y filtrar',
        body:
          'Busca por texto y, con el ícono de ajustes, filtra por tipo de caso.',
        placement: 'bottom',
      },
      {
        anchor: 'casos.columnas',
        title: 'Columnas de trabajo',
        body:
          'Sin comenzar, En ejecución, Por aprobar y Por finalizar. Arrastra una tarjeta para cambiarla de columna o haz clic para abrir el caso.',
        placement: 'top',
      },
    ],
  },

  // ── Expediente (casesnew/:id) ──────────────────────────────────────────────
  // Los cuatro comparten ruta: se ofrecen de a uno, en este orden.
  {
    // T5.
    id: 'caso.expediente',
    category: 'Expediente',
    title: 'El expediente del caso o alerta',
    description:
      'Entender los estados del caso y dónde está cada cosa: archivos, comentarios, aprobación y asistente.',
    icon: 'folder_open',
    routeMatch: 'casesnew/',
    chainTo: 'caso.cliente',
    chainAuto: true,
    audiencia: conUnidades,
    steps: [
      {
        // 'Rechazado' está comentado en el stepper: no se nombra.
        anchor: 'caso.estados',
        title: 'Estados',
        body:
          'Sin asignar, Asignado, En proceso, Por aprobar y Finalizado. El paso marcado es el estado actual.',
        placement: 'bottom',
      },
      {
        anchor: 'caso.pestanas',
        title: 'Pestañas del expediente',
        body:
          'Datos del cliente, de la alerta, comunicación, archivos, Immersion, Timeline y comentarios. Cambian según la configuración de la unidad.',
        placement: 'top',
      },
      {
        anchor: 'caso.tab.archivos',
        title: 'Archivos',
        body: 'La evidencia del caso: sube, descarga y comenta archivos.',
        placement: 'bottom',
      },
      {
        anchor: 'caso.tab.comentarios',
        title: 'Comentarios',
        body: 'La conversación del equipo sobre el caso.',
        placement: 'bottom',
      },
      {
        // Solo con el caso en proceso y sin aprobación pendiente.
        anchor: 'caso.tab.aprobacion',
        title: 'Solicitar aprobación',
        body:
          'Cuando terminas el trabajo, pide la aprobación del caso desde aquí.',
        placement: 'bottom',
      },
      {
        anchor: 'asistente.fab',
        title: 'Asistente IA',
        body:
          'Försker responde preguntas sobre el caso. Tiene su propio recorrido.',
        placement: 'left',
      },
    ],
  },

  // Un recorrido por pestaña (QA 05-10). Cada pestaña tiene además su botón
  // «¿Cómo funciona esto?» (app-tutorial-hint en case-tabs).
  {
    id: 'caso.cliente',
    category: 'Expediente',
    title: 'Datos del cliente',
    description:
      'Ubicar la identificación y los datos de contacto del cliente de la alerta.',
    icon: 'people',
    routeMatch: 'casesnew/',
    chainTo: 'caso.alerta',
    chainAuto: true,
    requiresAnchor: 'caso.tab.cliente',
    audiencia: conUnidades,
    steps: [
      {
        anchor: 'caso.tab.cliente',
        title: 'Pestaña Datos Cliente',
        body: 'Los datos del titular de la alerta. La abrimos por ti.',
        placement: 'bottom',
        clickAnchorOnEnter: true,
      },
      {
        anchor: 'cliente.identificacion',
        waitForAnchorMs: 3000,
        title: 'Identificación',
        body: 'CI o RIF, nombre y número de cliente: con esto se busca al cliente en el banco y en el grafo.',
        placement: 'left',
      },
      {
        anchor: 'cliente.contacto',
        title: 'Contacto',
        body: 'Tipo de persona, oficina, teléfonos y domicilio. Úsalos en la pestaña <b>Comunicación con el cliente</b>.',
        placement: 'right',
      },
    ],
  },

  {
    id: 'caso.alerta',
    category: 'Expediente',
    title: 'Datos de la alerta',
    description:
      'Leer qué disparó la alerta, el instrumento afectado y cómo se tipificó y gestionó.',
    icon: 'security',
    routeMatch: 'casesnew/',
    chainTo: 'caso.comunicacion',
    chainAuto: true,
    requiresAnchor: 'caso.tab.alerta',
    audiencia: conUnidades,
    steps: [
      {
        anchor: 'caso.tab.alerta',
        title: 'Pestaña Datos de la Alerta',
        body: 'Todo lo que trajo la alerta. La abrimos por ti.',
        placement: 'bottom',
        clickAnchorOnEnter: true,
      },
      {
        anchor: 'alerta.origen',
        waitForAnchorMs: 3000,
        title: 'Qué la disparó',
        body: 'Tipo de registro, descripción, instrumento de origen y de destino, y la tipificación (categoría y subtipo).',
        placement: 'right',
      },
      {
        anchor: 'alerta.gestion',
        title: 'Cómo se gestiona',
        body: 'Estado, fechas, canal, monto, prioridad, responsables y la decisión tomada.',
        placement: 'left',
      },
    ],
  },

  {
    id: 'caso.comunicacion',
    category: 'Expediente',
    title: 'Comunicación con el cliente',
    description:
      'Registrar cada llamada o contacto con el cliente y su resultado.',
    icon: 'question_answer',
    routeMatch: 'casesnew/',
    chainTo: 'caso.archivos',
    chainAuto: true,
    requiresAnchor: 'caso.tab.comunicacion',
    audiencia: (u) => unidadCon(u, 'comunicacion'),
    steps: [
      {
        anchor: 'caso.tab.comunicacion',
        title: 'Pestaña Comunicación',
        body: 'El historial de contactos con el cliente. La abrimos por ti.',
        placement: 'bottom',
        clickAnchorOnEnter: true,
      },
      {
        anchor: 'contacto.historial',
        waitForAnchorMs: 3000,
        title: 'Historial de contactos',
        body: 'Cada contacto con nombre, descripción, resultado, fechas y estado.',
        placement: 'top',
      },
      {
        anchor: 'contacto.agregar',
        title: 'Registrar un contacto',
        body: 'Agrega cada llamada o mensaje con su resultado: queda como evidencia en el caso.',
        placement: 'top',
      },
    ],
  },

  {
    id: 'caso.archivos',
    category: 'Expediente',
    title: 'Archivos del caso',
    description:
      'Subir la evidencia del caso, verla sin descargarla y comentarla.',
    icon: 'folder_open',
    routeMatch: 'casesnew/',
    chainTo: 'caso.immersion',
    chainAuto: true,
    requiresAnchor: 'caso.tab.archivos',
    audiencia: (u) => unidadCon(u, 'archivos'),
    steps: [
      {
        anchor: 'caso.tab.archivos',
        title: 'Pestaña Archivos',
        body: 'La evidencia del caso. La abrimos por ti.',
        placement: 'bottom',
        clickAnchorOnEnter: true,
      },
      {
        anchor: 'archivos.lista',
        waitForAnchorMs: 3000,
        title: 'Lista de archivos',
        body: 'Sube archivos con el botón de agregar y elige uno para verlo. También puedes descargarlo o eliminarlo.',
        placement: 'right',
      },
      {
        anchor: 'archivos.visor',
        title: 'Visor y comentarios',
        body: 'El archivo se ve aquí, en pestañas. Cada archivo tiene sus comentarios y se pueden enviar a los comentarios del caso.',
        placement: 'left',
      },
    ],
  },

  {
    id: 'caso.comentarios',
    category: 'Expediente',
    title: 'Comentarios del caso',
    description: 'Conversar con el equipo sobre el caso y dejar constancia.',
    icon: 'question_answer',
    routeMatch: 'casesnew/',
    chainTo: 'caso.historial',
    chainAuto: true,
    requiresAnchor: 'caso.tab.comentarios',
    audiencia: (u) => unidadCon(u, 'comentarios'),
    steps: [
      {
        anchor: 'caso.tab.comentarios',
        title: 'Pestaña Comentarios',
        body: 'La conversación del equipo. La abrimos por ti.',
        placement: 'bottom',
        clickAnchorOnEnter: true,
      },
      {
        anchor: 'comentarios.escribir',
        waitForAnchorMs: 3000,
        title: 'Escribir',
        body: 'Escribe y pulsa Enter. Se puede comentar en cualquier estado del caso.',
        placement: 'bottom',
      },
      {
        anchor: 'comentarios.lista',
        title: 'Comentarios',
        body: 'Los más recientes arriba, con autor y fecha. También llegan aquí los que envía el asistente o un archivo.',
        placement: 'top',
      },
    ],
  },

  {
    id: 'caso.historial',
    category: 'Expediente',
    title: 'Historial del caso',
    description:
      'Ver quién hizo qué y cuándo en el caso (bitácora de auditoría).',
    icon: 'assignment',
    routeMatch: 'casesnew/',
    chainTo: 'caso.asistente',
    chainAuto: true,
    requiresAnchor: 'caso.menu',
    audiencia: conUnidades,
    steps: [
      {
        anchor: 'caso.menu',
        title: 'Más opciones',
        body: 'En este menú están <b>Ver historial</b>, aprobar, asignar y tipificar, según tu rol. Pulsa Siguiente y abrimos el historial.',
        placement: 'left',
      },
      {
        anchor: 'historial.panel',
        hostAction: 'abrirHistorial',
        waitForAnchorMs: 3000,
        title: 'Historial',
        body: 'Cada acción del caso con su usuario y fecha: asignaciones, archivos, comentarios, bloqueos, aprobaciones. Ciérralo con la flecha.',
        placement: 'left',
      },
    ],
  },

  {
    id: 'caso.asistente',
    category: 'Expediente',
    title: 'Försker, el asistente IA del caso',
    description:
      'Preguntarle al asistente por el caso, citar entidades de Immersion y guardar sus respuestas como comentario.',
    icon: 'question_answer',
    routeMatch: 'casesnew/',
    requiresAnchor: 'asistente.fab',
    audiencia: conUnidades,
    steps: [
      {
        anchor: 'asistente.fab',
        title: 'Abrir el asistente',
        body: 'Este botón abre Försker. Pulsa Siguiente y lo abrimos por ti.',
        placement: 'left',
      },
      {
        anchor: 'asistente.mensajes',
        hostAction: 'abrirAsistente',
        waitForAnchorMs: 3000,
        title: 'La conversación',
        body:
          'Empieza con una sugerencia o escribe tu pregunta. Cuando hace falta, el asistente arma un <b>plan</b> y lo ejecuta solo, paso a paso. Cada respuesta se puede guardar como comentario del caso.',
        placement: 'left',
      },
      {
        anchor: 'asistente.entrada',
        title: 'Escribir',
        body:
          'Escribe aquí. Usa <b>@</b> para citar una cuenta o persona de Immersion, y Shift + Enter para una nueva línea.',
        placement: 'left',
      },
      {
        anchor: 'asistente.hilos',
        title: 'Chats del caso',
        body: 'Cambia entre los chats del caso o empieza uno nuevo.',
        placement: 'left',
      },
      {
        anchor: 'asistente.ayuda',
        title: '¿Qué puede hacer?',
        body: 'Aquí ves la lista de acciones que el asistente sabe hacer.',
        placement: 'left',
      },
    ],
  },

  {
    id: 'caso.immersion',
    category: 'Expediente',
    title: 'Immersion: el grafo del caso',
    description:
      'Importar transferencias, ordenar y recorrer el grafo, y usar sus paneles (entidades, historial, comentarios).',
    icon: 'account_tree',
    routeMatch: 'casesnew/',
    chainTo: 'caso.versiones',
    chainAuto: true,
    requiresAnchor: 'caso.tab.immersion',
    audiencia: (u) => unidadCon(u, 'grafo'),
    steps: [
      {
        anchor: 'caso.tab.immersion',
        title: 'Pestaña Immersion',
        body: 'El grafo del caso vive en esta pestaña. La abrimos por ti.',
        placement: 'bottom',
        clickAnchorOnEnter: true,
      },
      {
        anchor: 'immersion.toolbar',
        waitForAnchorMs: 3000,
        title: 'Barra de herramientas',
        body:
          'Orden del grafo (libre, circular, árbol), captura, importar, exportar, guardar versión y tamaño del texto. Se arrastra por el asa y se contrae con la flecha.',
        placement: 'bottom',
      },
      {
        anchor: 'immersion.importar',
        title: 'Importar transferencias',
        body: 'Muestra el formato, descarga la plantilla y valida el Excel antes de agregar sus transferencias al grafo.',
        placement: 'bottom',
      },
      {
        anchor: 'immersion.paneles',
        title: 'Paneles',
        body:
          'Historial de versiones, comentarios, entidades, bloqueos y montos se abren a la derecha, de a uno.',
        placement: 'bottom',
      },
      {
        anchor: 'immersion.navegacion',
        title: 'Moverse por el grafo',
        body: 'Acerca, aleja y centra la vista. Las flechas de al lado la desplazan.',
        placement: 'left',
      },
      {
        anchor: 'immersion.lienzo',
        title: 'El lienzo',
        body:
          'Clic derecho sobre un nodo para pedir más transferencias, bloquear la cuenta o eliminarlo.',
        placement: 'center',
      },
    ],
  },

  {
    id: 'caso.versiones',
    category: 'Expediente',
    title: 'Versiones de Immersion',
    description:
      'Guardar una versión del grafo y volver a una anterior desde el historial de versiones.',
    icon: 'account_tree',
    routeMatch: 'casesnew/',
    chainTo: 'caso.timeline',
    chainAuto: true,
    requiresAnchor: 'caso.tab.immersion',
    audiencia: (u) => unidadCon(u, 'grafo'),
    steps: [
      {
        anchor: 'caso.tab.immersion',
        title: 'Pestaña Immersion',
        body: 'Las versiones son del grafo. Abrimos Immersion por ti.',
        placement: 'bottom',
        clickAnchorOnEnter: true,
      },
      {
        anchor: 'immersion.guardar-version',
        waitForAnchorMs: 3000,
        title: 'Guardar versión',
        body: 'Guarda el grafo como está: nodos, relaciones, posiciones y lo que activaste o desactivaste. Solo el analista asignado, con el caso en proceso.',
        placement: 'bottom',
      },
      {
        anchor: 'immersion.paneles',
        title: 'Historial de versiones',
        body: 'Este botón abre la lista de versiones guardadas. Pulsa Siguiente y la abrimos.',
        placement: 'bottom',
      },
      {
        anchor: 'immersion.historial',
        hostAction: 'abrirHistorialVersiones',
        waitForAnchorMs: 3000,
        title: 'Volver a una versión',
        body: '<b>Restaurar</b> carga esa versión del grafo; <b>Cambiar a versión actual</b> vuelve a la última.',
        placement: 'left',
      },
    ],
  },

  {
    id: 'caso.timeline',
    category: 'Expediente',
    title: 'Timeline del caso',
    description:
      'Revisar la actividad del caso en orden cronológico y filtrarla por rango de fechas.',
    icon: 'timeline',
    routeMatch: 'casesnew/',
    chainTo: 'caso.comentarios',
    chainAuto: true,
    requiresAnchor: 'caso.tab.timeline',
    audiencia: (u) => unidadCon(u, 'timeline'),
    steps: [
      {
        anchor: 'caso.tab.timeline',
        title: 'Pestaña Timeline',
        body: 'La actividad del caso vive en esta pestaña. La abrimos por ti.',
        placement: 'bottom',
        clickAnchorOnEnter: true,
      },
      {
        anchor: 'timeline.rango',
        waitForAnchorMs: 3000,
        title: 'Rango de fechas',
        body:
          'Hoy, 7, 14 o 30 días. <b>Personalizado</b> abre la búsqueda por identificación y por fechas.',
        placement: 'bottom',
      },
      {
        anchor: 'timeline.lista',
        title: 'Actividades',
        body:
          'Cada actividad con su fecha, de la más reciente a la más antigua. Al bajar se cargan más.',
        placement: 'top',
      },
      {
        anchor: 'timeline.detalle',
        title: 'Detalle',
        body: 'Despliega una actividad para ver qué cambió.',
        placement: 'left',
      },
    ],
  },

  // ── Análisis ───────────────────────────────────────────────────────────────
  {
    // T6.
    id: 'analisis.bandeja',
    category: 'Análisis',
    title: 'Análisis: casos por asignar',
    description:
      'Repartir los casos de análisis y seguirlos por estado hasta finalizarlos.',
    icon: 'assignment',
    routeMatch: '/withoutAssigned/',
    audiencia: esAnalisis,
    steps: [
      {
        anchor: 'analisis.buscador',
        title: 'Buscar y filtrar',
        body: 'Busca casos por texto y abre los filtros con el ícono de ajustes.',
        placement: 'bottom',
      },
      {
        anchor: 'analisis.estados',
        title: 'Estados',
        body:
          'Sin Asignar, Asignado, En proceso, Por aprobar y Finalizadas, con su contador.',
        placement: 'bottom',
      },
      {
        anchor: 'analisis.lista',
        title: 'Casos',
        body: 'Haz clic en un caso para abrir su expediente.',
        placement: 'top',
      },
    ],
  },

  // ── RAS ────────────────────────────────────────────────────────────────────
  {
    // T7. OJO: al hacer login un RAS probablemente aterriza en /dashboard (ve
    // T1) y llega a /resumeRas recién con F5. Ver QA.
    id: 'ras.resumen',
    category: 'RAS',
    title: 'Resumen LDC',
    description:
      'Leer el resumen de la unidad: indicadores, casos por analista y estado de los casos.',
    icon: 'donut_large',
    routeMatch: '/resumeRas',
    audiencia: esRas,
    steps: [
      {
        anchor: 'ras.indicadores',
        title: 'Indicadores',
        body: 'Los números principales de la unidad.',
        placement: 'bottom',
      },
      {
        anchor: 'ras.carrusel',
        title: 'Casos por analista',
        body:
          'Carga de cada analista y los casos de los últimos 7 días. Pasa de una vista a otra con el carrusel.',
        placement: 'right',
      },
      {
        anchor: 'ras.estado',
        title: 'Estado de los casos',
        body: 'Distribución de los casos por estado.',
        placement: 'left',
      },
      {
        anchor: 'general.usuario',
        title: 'Tu usuario',
        body: 'Desde tu foto abres <b>Perfil</b> y <b>Salir</b>.',
        placement: 'bottom',
      },
    ],
  },

  // ── Administración (T8, partido por pantalla: cada una con su permiso) ─────
  {
    id: 'admin.usuarios',
    category: 'Administración',
    title: 'Gestión de usuarios',
    description: 'Dar de alta usuarios, asignarles rol y licencia.',
    icon: 'people',
    // Exacto: '/users' a secas también coincidiría con otras rutas por prefijo.
    routeMatch: /^\/users$/,
    screenMatch: '/users',
    audiencia: (u) => !!(u.role && getSeeUsers(u.role)),
    steps: [
      {
        anchor: 'admin.usuarios.licencias',
        title: 'Licencias',
        body: 'Licencias disponibles y su uso.',
        placement: 'bottom',
      },
      {
        anchor: 'admin.usuarios.tabla',
        title: 'Usuarios',
        body:
          'Lista de usuarios con su rol y licencia. Filtra por texto; en cada fila puedes editar, desbloquear o eliminar.',
        placement: 'top',
      },
      {
        anchor: 'admin.usuarios.crear',
        title: 'Nuevo usuario',
        body: 'Crea un usuario y asígnale rol.',
        placement: 'left',
      },
    ],
  },

  {
    id: 'admin.roles',
    category: 'Administración',
    title: 'Roles y permisos',
    description:
      'Crear roles y definir qué puede ver y hacer cada uno (unidades y permisos).',
    icon: 'security',
    // Exacto: '/roles' coincidiría también con '/rolesView' (vista RAS).
    routeMatch: /^\/roles$/,
    screenMatch: '/roles',
    audiencia: (u) => !!(u.role && getSeeRoles(u.role)),
    steps: [
      {
        anchor: 'admin.roles.tabla',
        title: 'Roles',
        body:
          'Cada rol junta unidades y permisos. Lo que un usuario ve en el menú depende de su rol.',
        placement: 'top',
      },
      {
        anchor: 'admin.roles.crear',
        title: 'Nuevo rol',
        body: 'Crea un rol y marca sus unidades y permisos.',
        placement: 'left',
      },
    ],
  },

  {
    id: 'admin.unidades',
    category: 'Administración',
    title: 'Unidades',
    description: 'Crear y editar unidades y entrar a sus tipos de caso.',
    icon: 'business',
    // Exacto: '/units' coincidiría también con '/unitsView' (vista RAS).
    routeMatch: /^\/units$/,
    screenMatch: '/units',
    audiencia: (u) => !!(u.role && getSeeUnits(u.role)),
    steps: [
      {
        anchor: 'admin.unidades.tabla',
        title: 'Unidades',
        body:
          'Lista de unidades. En cada fila: editar, eliminar y el ojo para ver sus tipos de caso.',
        placement: 'top',
      },
      {
        anchor: 'admin.unidades.crear',
        title: 'Nueva unidad',
        body: 'Crea una unidad.',
        placement: 'left',
      },
    ],
  },
];
